import { useEffect } from 'react';
import type { Map } from 'maplibre-gl';
import useGeoData from '../../../store/useGeoData'; // Corretto import (default)
import { FeatureCollection } from 'geojson';
import type { GeoDataState } from '../../../store/types';

const SOURCE_ID = 'contours-source';
const LAYER_ID = 'contours-layer';

/**
 * Hook per gestire la visualizzazione del layer delle curve di livello sulla mappa.
 * Aggiunge, aggiorna o rimuove la sorgente e il layer MapLibre
 * in base alla presenza di `resultGeojson` nello store `contoursSlice`.
 *
 * @param map L'istanza della mappa MapLibre.
 */
export const useContoursLayer = (map: Map | null) => {
  // Accediamo direttamente alla proprietà esposta dallo store combinato
  // Rimosso 'shallow' per compatibilità con la firma attuale dell'hook
  const resultGeojson = useGeoData(
    (state: GeoDataState) => state.resultGeojson as FeatureCollection | null
  );

  useEffect(() => {
    if (!map || !map.isStyleLoaded()) {
      // console.log('useContoursLayer: Map not ready or style not loaded');
      return;
    }

    const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
    const layer = map.getLayer(LAYER_ID);

    // console.log('useContoursLayer: Effect triggered', { mapReady: map?.isStyleLoaded(), resultGeojson, sourceExists: !!source, layerExists: !!layer });


    if (resultGeojson) {
      // console.log('useContoursLayer: resultGeojson found, adding/updating source and layer');
      if (source) {
        // console.log('useContoursLayer: Source exists, updating data');
        source.setData(resultGeojson);
      } else {
        // console.log('useContoursLayer: Source does not exist, adding source');
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: resultGeojson,
        });
      }

      if (!layer) {
        // console.log('useContoursLayer: Layer does not exist, adding layer');
        map.addLayer({
          id: LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            // Esempio: colore basato sull'elevazione (se presente)
            // 'line-color': [
            //   'interpolate',
            //   ['linear'],
            //   ['get', 'elevation'], // Assicurati che la proprietà esista nel tuo GeoJSON
            //   0, '#0000ff', // Blu per elevazione bassa
            //   1000, '#ff0000', // Rosso per elevazione alta
            // ],
            'line-color': '#e53e3e', // Rosso di default
            'line-width': 1.5,
            'line-opacity': 0.8,
          },
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          }
        });
      }
    } else {
      // console.log('useContoursLayer: resultGeojson is null, removing layer and source if they exist');
      if (layer) {
        // console.log('useContoursLayer: Removing layer');
        map.removeLayer(LAYER_ID);
      }
      if (source) {
        // console.log('useContoursLayer: Removing source');
        // Sembra esserci un problema nel rimuovere la sorgente subito dopo il layer
        // Aggiungiamo un piccolo ritardo per sicurezza, anche se non ideale
        // setTimeout(() => {
        //   if (map.getSource(SOURCE_ID)) { // Ricontrolla se esiste ancora
        //     map.removeSource(SOURCE_ID);
        //     console.log('useContoursLayer: Source removed after delay');
        //   }
        // }, 100);
         // Rimuoviamo la sorgente direttamente, MapLibre dovrebbe gestirlo
         map.removeSource(SOURCE_ID);
      }
    }

    // Funzione di cleanup
    return () => {
      // console.log('useContoursLayer: Cleanup function running');
      if (map && map.getStyle()) { // Verifica che la mappa e lo stile esistano ancora
        try {
          const currentLayer = map.getLayer(LAYER_ID);
          if (currentLayer) {
            // console.log('useContoursLayer: Cleanup - Removing layer');
            map.removeLayer(LAYER_ID);
          }
          const currentSource = map.getSource(SOURCE_ID);
          if (currentSource) {
            // console.log('useContoursLayer: Cleanup - Removing source');
            map.removeSource(SOURCE_ID);
          }
        } catch (error) {
          // console.error('useContoursLayer: Error during cleanup:', error);
          // Potrebbe accadere se la mappa viene distrutta prima del cleanup
        }
      }
    };
  }, [map, resultGeojson]); // Dipendenze: mappa e GeoJSON risultato
};